import React, { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { format, parse, isValid } from "date-fns";
import {
  Input,
  Dropdown,
  Option,
  OptionGroup,
  Checkbox,
  Button,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import styled from "styled-components";

const CompactFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.semanticColors.bodyText};
  margin: 0;
`;

// Special styling for dropdown background colors
const StyledDropdown = styled(Dropdown)<{ $backgroundColor?: string }>`
  min-width: unset !important;
  width: 100% !important;

  .fui-Dropdown__listbox {
    min-width: unset !important;
    width: 100% !important;
  }

  .fui-Dropdown__button {
    box-shadow: none !important;
    background: ${(props) =>
      props.$backgroundColor ||
      props.theme.semanticColors.inputBackground} !important;

    &:hover {
      border-color: ${({ theme }) => theme.semanticColors.link} !important;
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
    }

    &:focus {
      border-color: ${({ theme }) => theme.semanticColors.link} !important;
      box-shadow: 0 0 0 2px
        ${({ theme }) =>
          theme.isDark
            ? "rgba(56, 169, 255, 0.2)"
            : "rgba(0, 120, 212, 0.2)"} !important;
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
    }

    &:active {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
    }

    .fui-Dropdown__button-content {
      background: transparent !important;
    }

    .fui-Dropdown__expandIcon {
      background: transparent !important;
    }
  }

  &[aria-expanded="true"] .fui-Dropdown__button {
    background: ${(props) =>
      props.$backgroundColor ||
      props.theme.semanticColors.inputBackground} !important;
  }
`;

// Input with icon styling
const IconInputContainer = styled.div<{
  $backgroundColor?: string;
  $hasError?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;

  .fui-Input {
    width: 100%;
    padding-left: 32px;
    background: ${(props) =>
      props.$backgroundColor ||
      props.theme.semanticColors.inputBackground} !important;
    border-color: ${(props) =>
      props.$hasError
        ? props.theme.semanticColors.errorText
        : props.theme.isDark
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.12)"} !important;

    &:hover {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
      border-color: ${(props) =>
        props.$hasError
          ? props.theme.semanticColors.errorText
          : props.theme.isDark
          ? "rgba(255, 255, 255, 0.15)"
          : "rgba(0, 0, 0, 0.18)"} !important;
    }

    &:focus {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
      border-color: ${(props) =>
        props.$hasError
          ? props.theme.semanticColors.errorText
          : props.theme.semanticColors.link} !important;
    }
  }
`;

const IconContainer = styled.div<{ $clickable?: boolean }>`
  position: absolute;
  left: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  pointer-events: ${(props) => (props.$clickable ? "auto" : "none")};
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};

  &:hover {
    color: ${(props) =>
      props.$clickable
        ? props.theme.semanticColors.link
        : props.theme.semanticColors.bodySubtext};
  }
`;

// Input with unit styling
const UnitInputContainer = styled.div<{
  $backgroundColor?: string;
  $hasError?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid
    ${(props) =>
      props.$hasError
        ? props.theme.semanticColors.errorText
        : props.theme.isDark
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.12)"};
  border-radius: 4px;
  background: ${(props) =>
    props.$backgroundColor ||
    props.theme.semanticColors.inputBackground} !important;

  &:hover {
    border-color: ${(props) =>
      props.$hasError
        ? props.theme.semanticColors.errorText
        : props.theme.isDark
        ? "rgba(255, 255, 255, 0.15)"
        : "rgba(0, 0, 0, 0.18)"};
  }

  &:focus-within {
    border-color: ${(props) =>
      props.$hasError
        ? props.theme.semanticColors.errorText
        : props.theme.semanticColors.link};
    box-shadow: 0 0 0 2px
      ${({ theme }) =>
        theme.isDark ? "rgba(56, 169, 255, 0.2)" : "rgba(0, 120, 212, 0.2)"};
  }

  .fui-Input {
    flex: 1;
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    padding-right: 8px;

    &:hover {
      border: none !important;
      background: transparent !important;
    }

    &:focus {
      border: none !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    input {
      text-align: right !important;
    }
  }
`;

const UnitContainer = styled.div`
  padding: 0 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  font-weight: 500;
  white-space: nowrap;
  border-left: 1px solid
    ${({ theme }) =>
      theme.isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.12)"};
  display: flex;
  align-items: center;
  height: 24px;
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
`;

// Input with button styling
const ButtonInputContainer = styled.div<{ $backgroundColor?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;

  .fui-Input {
    flex: 1;
    background: ${(props) =>
      props.$backgroundColor ||
      props.theme.semanticColors.inputBackground} !important;

    &:hover {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
    }

    &:focus {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
    }
  }

  .fui-Button {
    flex-shrink: 0;
    min-width: auto;
    padding: 0 8px;
    height: 32px;
  }
`;

// Vehicle value container
const VehicleValueContainer = styled.div<{ $backgroundColor?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;

  .value-input {
    flex: 1;
    background: ${(props) =>
      props.$backgroundColor ||
      props.theme.semanticColors.inputBackground} !important;

    &:hover {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
    }

    &:focus {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
    }
  }

  .currency-dropdown {
    min-width: 80px;
  }

  .vat-checkbox {
    white-space: nowrap;
  }
`;

// Checkbox group container
const CheckboxGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Regular input styling
const StyledInput = styled(Input)<{
  $backgroundColor?: string;
  $hasError?: boolean;
}>`
  background: ${(props) =>
    props.$backgroundColor ||
    props.theme.semanticColors.inputBackground} !important;

  input {
    background: ${(props) =>
      props.$backgroundColor ||
      props.theme.semanticColors.inputBackground} !important;
    border-color: ${(props) =>
      props.$hasError
        ? props.theme.semanticColors.errorText
        : props.theme.isDark
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.12)"} !important;

    &:hover {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
      border-color: ${(props) =>
        props.$hasError
          ? props.theme.semanticColors.errorText
          : props.theme.isDark
          ? "rgba(255, 255, 255, 0.15)"
          : "rgba(0, 0, 0, 0.18)"} !important;
    }

    &:focus {
      background: ${(props) =>
        props.$backgroundColor ||
        props.theme.semanticColors.inputBackground} !important;
      border-color: ${(props) =>
        props.$hasError
          ? props.theme.semanticColors.errorText
          : props.theme.semanticColors.link} !important;
    }
  }
`;

const ErrorMessage = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.semanticColors.errorText};
  margin-top: 2px;
`;

interface CheckboxOption {
  name: string;
  label: string;
  translationKey?: string;
}

interface CompactFieldProps {
  type:
    | "input"
    | "dropdown"
    | "checkbox"
    | "date"
    | "inputWithIcon"
    | "inputWithUnit"
    | "inputWithButton"
    | "inputWithValidation"
    | "vehicleValue"
    | "checkboxGroup";
  name: string;
  label?: string;
  translationKey?: string;
  placeholder?: string;
  options?: Array<{
    key: string;
    value?: string; // Optional: if provided, used as form value instead of key
    text: string;
    disabled?: boolean;
    isGroup?: boolean;
    children?: Array<{ key: string; value?: string; text: string }>;
  }>;
  defaultValue?: string | boolean;
  defaultChecked?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  inputType?:
    | "number"
    | "search"
    | "time"
    | "text"
    | "date"
    | "tel"
    | "url"
    | "email"
    | "password"
    | "datetime-local"
    | "month"
    | "week";
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  backgroundColor?: string;
  required?: boolean;

  // Icon-specific props
  icon?: React.ReactNode;
  iconTitle?: string;
  onIconClick?: () => void;

  // Unit-specific props
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  // Called after the field loses focus (currently supported by "inputWithUnit")
  onBlur?: () => void;

  // Button-specific props
  buttonText?: string;
  buttonIcon?: React.ReactNode;
  onButtonClick?: (value: string) => void;
  buttonVariant?: "primary" | "secondary" | "outline" | "subtle";

  // Validation-specific props
  pattern?: RegExp;
  minLength?: number;
  maxLength?: number;
  customValidation?: (value: string) => string | undefined;

  // Vehicle value specific props
  currencyOptions?: Array<{ key: string; text: string }>;

  /** In-app guide anchor (data-help) on the field's root, label included. */
  dataHelp?: string;
  currencyName?: string;
  vatCheckboxName?: string;
  vatLabel?: string;

  // Checkbox group specific props
  checkboxes?: CheckboxOption[];
}

const CompactField: React.FC<CompactFieldProps> = ({
  type,
  name,
  label,
  translationKey,
  placeholder,
  options = [],
  defaultValue,
  defaultChecked,
  readOnly,
  disabled,
  inputType = "text",
  style,
  inputStyle,
  backgroundColor,
  required,
  icon,
  iconTitle,
  onIconClick,
  unit,
  min,
  max,
  step,
  buttonText,
  buttonIcon,
  onButtonClick,
  buttonVariant = "outline",
  pattern,
  minLength,
  maxLength,
  customValidation,
  currencyOptions = [],
  currencyName,
  vatCheckboxName,
  vatLabel,
  checkboxes = [],
  onBlur,
  dataHelp,
}) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();
  const { t } = useTranslation();

  const displayLabel = translationKey ? t(translationKey) : label;
  const fieldError = errors[name];
  // Only watch the field value if we have a button that needs it
  // This prevents unnecessary re-renders for date fields
  const currentValue = onButtonClick ? watch(name) : undefined;

  // Memoize DatePicker callbacks to prevent infinite re-renders
  const formatDateCallback = useCallback((date?: Date) => {
    return date && isValid(date) ? format(date, "dd.MM.yyyy") : "";
  }, []);

  const parseDateFromStringCallback = useCallback((dateStr: string) => {
    const parsed = parse(dateStr.trim(), "dd.MM.yyyy", new Date());
    return isValid(parsed) ? parsed : null;
  }, []);

  // Helper function to get display text for dropdown options
  const getDisplayText = (
    key: string,
    optionsList: Array<{ key: string; value?: string; text: string }> = options
  ): string => {
    // First try to find by value (if value property exists and matches)
    const optionByValue = optionsList.find((opt) => opt.value === key);
    if (optionByValue) {
      return optionByValue.text;
    }
    // Fallback to finding by key
    const optionByKey = optionsList.find((opt) => opt.key === key);
    return optionByKey?.text || key;
  };

  // Validation rules for inputWithValidation
  const getValidationRules = () => {
    const rules: any = {};

    if (required) {
      rules.required = t("validation.required");
    }

    if (pattern) {
      rules.pattern = {
        value: pattern,
        message: t("validation.invalidFormat"),
      };
    }

    if (minLength) {
      rules.minLength = {
        value: minLength,
        message: t("validation.minLength", { count: minLength }),
      };
    }

    if (maxLength) {
      rules.maxLength = {
        value: maxLength,
        message: t("validation.maxLength", { count: maxLength }),
      };
    }

    if (min !== undefined) {
      rules.min = {
        value: min,
        message: t("validation.minValue", { value: min }),
      };
    }

    if (max !== undefined) {
      rules.max = {
        value: max,
        message: t("validation.maxValue", { value: max }),
      };
    }

    if (customValidation) {
      rules.validate = customValidation;
    }

    return rules;
  };

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick(currentValue || "");
    }
  };

  const handleIconClick = () => {
    if (onIconClick) {
      onIconClick();
    }
  };

  const renderField = () => {
    switch (type) {
      case "dropdown":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{ required }}
            render={({ field }) => {
              const currentDisplayText = field.value
                ? getDisplayText(field.value)
                : "";

              return (
                <StyledDropdown
                  placeholder={placeholder}
                  value={currentDisplayText}
                  selectedOptions={field.value ? [field.value] : []}
                  onOptionSelect={(_, data) => {
                    if (data.optionValue) {
                      field.onChange(data.optionValue);
                    }
                  }}
                  size="small"
                  style={inputStyle}
                  $backgroundColor={backgroundColor}
                  disabled={readOnly}
                >
                  {options.map((option) => {
                    // If option has children, render as OptionGroup
                    if (option.children && option.children.length > 0) {
                      return (
                        <OptionGroup key={option.key} label={option.text}>
                          {option.children.map((child) => (
                            <Option
                              key={child.key}
                              value={child.value ?? child.key}
                              text={child.text}
                            >
                              {child.text}
                            </Option>
                          ))}
                        </OptionGroup>
                      );
                    }
                    // Otherwise render as regular Option
                    return (
                      <Option
                        key={option.key}
                        value={option.value ?? option.key}
                        text={option.text}
                        disabled={option.disabled}
                      >
                        {option.text}
                      </Option>
                    );
                  })}
                </StyledDropdown>
              );
            }}
          />
        );

      case "inputWithIcon":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{ required }}
            render={({ field }) => (
              <IconInputContainer
                $backgroundColor={backgroundColor}
                $hasError={!!fieldError}
              >
                {icon && (
                  <IconContainer
                    $clickable={!!onIconClick}
                    onClick={handleIconClick}
                    title={iconTitle}
                  >
                    {icon}
                  </IconContainer>
                )}
                <Input
                  {...field}
                  type={inputType}
                  placeholder={placeholder}
                  readOnly={readOnly}
                  size="small"
                  style={{ ...style, ...inputStyle }}
                />
              </IconInputContainer>
            )}
          />
        );

      case "inputWithUnit":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{ required, min, max }}
            render={({ field }) => (
              <UnitInputContainer
                $backgroundColor={backgroundColor}
                $hasError={!!fieldError}
              >
                <Input
                  {...field}
                  onBlur={() => {
                    field.onBlur();
                    if (!readOnly) onBlur?.();
                  }}
                  type={inputType}
                  placeholder={placeholder}
                  readOnly={readOnly}
                  size="small"
                  style={{ textAlign: "right", ...style, ...inputStyle }}
                  min={min}
                  max={max}
                  step={step}
                />
                {unit && <UnitContainer>{unit}</UnitContainer>}
              </UnitInputContainer>
            )}
          />
        );

      case "inputWithButton":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{ required }}
            render={({ field }) => (
              <ButtonInputContainer $backgroundColor={backgroundColor}>
                <Input
                  {...field}
                  type={inputType}
                  placeholder={placeholder}
                  readOnly={readOnly}
                  size="small"
                  style={{ ...style, ...inputStyle }}
                />
                <Button
                  appearance={buttonVariant}
                  size="small"
                  icon={buttonIcon}
                  onClick={handleButtonClick}
                >
                  {buttonText}
                </Button>
              </ButtonInputContainer>
            )}
          />
        );

      case "vehicleValue":
        return (
          <VehicleValueContainer $backgroundColor={backgroundColor}>
            <Controller
              name={name}
              control={control}
              defaultValue={defaultValue}
              rules={{ required }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  placeholder={placeholder}
                  readOnly={readOnly}
                  size="small"
                  className="value-input"
                  style={{ ...style, ...inputStyle }}
                />
              )}
            />
            {currencyName && currencyOptions.length > 0 && (
              <Controller
                name={currencyName}
                control={control}
                defaultValue={currencyOptions[0]?.key}
                render={({ field }) => {
                  const currencyDisplayText = field.value
                    ? getDisplayText(field.value, currencyOptions)
                    : "";

                  return (
                    <StyledDropdown
                      placeholder={placeholder}
                      value={currencyDisplayText}
                      selectedOptions={field.value ? [field.value] : []}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) {
                          field.onChange(data.optionValue);
                        }
                      }}
                      size="small"
                      className="currency-dropdown"
                      $backgroundColor={backgroundColor}
                      disabled={readOnly}
                    >
                      {currencyOptions.map((option) => (
                        <Option
                          key={option.key}
                          value={option.key}
                          text={option.text}
                        >
                          {option.text}
                        </Option>
                      ))}
                    </StyledDropdown>
                  );
                }}
              />
            )}
            {vatCheckboxName && vatLabel && (
              <Controller
                name={vatCheckboxName}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    onChange={(_, data) => field.onChange(data.checked)}
                    label={vatLabel}
                    className="vat-checkbox"
                    disabled={readOnly}
                  />
                )}
              />
            )}
          </VehicleValueContainer>
        );

      case "checkboxGroup":
        return (
          <CheckboxGroupContainer>
            {checkboxes.map((checkbox) => (
              <CheckboxRow key={checkbox.name}>
                <Controller
                  name={checkbox.name}
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value || false}
                      onChange={(_, data) => field.onChange(data.checked)}
                      label={
                        checkbox.translationKey
                          ? t(checkbox.translationKey)
                          : checkbox.label
                      }
                      disabled={readOnly}
                    />
                  )}
                />
              </CheckboxRow>
            ))}
          </CheckboxGroupContainer>
        );

      case "inputWithValidation":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={getValidationRules()}
            render={({ field }) => (
              <StyledInput
                {...field}
                type={inputType}
                placeholder={placeholder}
                readOnly={readOnly}
                size="small"
                style={{ ...style, ...inputStyle }}
                $backgroundColor={backgroundColor}
                $hasError={!!fieldError}
                min={min}
                max={max}
              />
            )}
          />
        );

      case "checkbox":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultChecked || false}
            render={({ field }) => (
              <Checkbox
                checked={field.value || false}
                onChange={(_, data) => field.onChange(data.checked)}
                label={displayLabel}
                disabled={readOnly}
              />
            )}
          />
        );

      case "date":
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{ required }}
            render={({ field }) => {
              // Parse date value once per render
              const dateValue = field.value
                ? parse(field.value, "yyyy-MM-dd", new Date())
                : null;

              return (
                <DatePicker
                  value={dateValue}
                  onSelectDate={(date: Date | null | undefined) => {
                    if (date && isValid(date)) {
                      const isoDate = format(date, "yyyy-MM-dd");
                      field.onChange(isoDate);
                    } else {
                      field.onChange(null);
                    }
                  }}
                  formatDate={formatDateCallback}
                  parseDateFromString={parseDateFromStringCallback}
                  allowTextInput
                  placeholder=""
                  disabled={disabled || readOnly}
                  style={{ width: inputStyle?.width || "150px", ...style }}
                />
              );
            }}
          />
        );

      default:
        return (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{ required }}
            render={({ field }) => (
              <StyledInput
                {...field}
                type={inputType}
                placeholder={placeholder}
                readOnly={readOnly}
                size="small"
                style={{ ...style, ...inputStyle }}
                $backgroundColor={backgroundColor}
                $hasError={!!fieldError}
                onChange={(e) => {
                  // For number inputs, convert to number type
                  if (inputType === "number") {
                    const value = e.target.value;
                    field.onChange(value === "" ? "" : Number(value));
                  } else {
                    field.onChange(e.target.value);
                  }
                }}
                value={field.value ?? ""}
              />
            )}
          />
        );
    }
  };

  return (
    <CompactFieldWrapper style={style} data-help={dataHelp}>
      {displayLabel && type !== "checkbox" && type !== "checkboxGroup" && (
        <FieldLabel>
          {displayLabel}
          {required && <span style={{ color: "#d13438" }}>*</span>}
        </FieldLabel>
      )}
      {renderField()}
      {fieldError && type === "inputWithValidation" && (
        <ErrorMessage>{fieldError.message as string}</ErrorMessage>
      )}
    </CompactFieldWrapper>
  );
};

export default CompactField;
